import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'theme';

type ThemeMode = 'auto' | 'light' | 'dark';

function hasMatchMedia(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function';
}

function getSystemPrefersDark(): boolean {
  if (!hasMatchMedia()) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function getInitialMode(): ThemeMode {
  if (typeof window === 'undefined') return 'auto';
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved === 'dark' || saved === 'light') return saved;
  return 'auto';
}

/**
 * Single source of truth for GRID dark mode.
 *
 * Behavior (macOS/GitHub-style):
 * - Default ('auto'): follows the OS color scheme live, including changes
 *   after the initial load.
 * - Once the user toggles manually, that choice ('light'/'dark') is
 *   persisted and wins over the OS indefinitely.
 *
 * Exposes the same shape as the legacy hooks it replaces so existing
 * consumers (e.g. the header toggle) don't need to change:
 *   { theme: 'light' | 'dark', toggleTheme(), isDark: boolean }
 */
export function useTheme() {
  const [mode, setMode] = useState<ThemeMode>(getInitialMode);
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(getSystemPrefersDark);

  useEffect(() => {
    if (!hasMatchMedia()) return undefined;

    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event: MediaQueryListEvent) => {
      setSystemPrefersDark(event.matches);
    };

    mql.addEventListener('change', handleChange);
    return () => {
      mql.removeEventListener('change', handleChange);
    };
  }, []);

  const isDark = mode === 'auto' ? systemPrefersDark : mode === 'dark';

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [isDark]);

  const toggleTheme = useCallback(() => {
    setMode((prevMode) => {
      const currentIsDark = prevMode === 'auto' ? getSystemPrefersDark() : prevMode === 'dark';
      const nextMode: ThemeMode = currentIsDark ? 'light' : 'dark';
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, nextMode);
      }
      return nextMode;
    });
  }, []);

  return {
    theme: (isDark ? 'dark' : 'light') as 'light' | 'dark',
    toggleTheme,
    isDark,
  };
}
